import type {ReactElement} from 'react';
import React, {useCallback} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {TimelineItem} from '../timeline-item/timeline-item';
import {TimelineItemType} from '../../timeline.types';
import {PageThumbnail} from '../page-thumbnail/page-thumbnail';
import {useAppSelector} from '@/hooks';
import type {DragModifyingData, ResizeModifyingData, ResizeCalculationParams, ResizeCalculationResult} from '../timeline-item/timeline-item.types';
import styles from './page-timeline-item.module.scss';

interface PageTimelineItemProps {
  id: string;
  startTime: number;
  endTime: number;
  minPageDuration?: number;
  subRow: number;
  onClick?: (id: string) => void;
  onDragStart?: (id: string) => void;
  onDragMove?: (id: string, data: DragModifyingData) => void;
  onDragEnd?: (id: string, data: DragModifyingData) => Promise<void>;
  onResizeEnd?: (id: string, data: ResizeModifyingData) => Promise<void>;
}

const DEFAULT_MIN_PAGE_DURATION = 0.5;

export function PageTimelineItem({
  id,
  startTime,
  endTime,
  minPageDuration = DEFAULT_MIN_PAGE_DURATION,
  subRow,
  onClick,
  onDragStart,
  onDragMove,
  onDragEnd,
  onResizeEnd,
}: PageTimelineItemProps): ReactElement {
  const selectedTimelineItem = useAppSelector((state) => state.timeline.selectedTimelineItem);
  const currentPageId = useAppSelector((state) => state.posterEditor.currentPageId);
  const isSelected = selectedTimelineItem?.type === TimelineItemType.PAGE && selectedTimelineItem?.id === id;
  const isCurrentPage = id === currentPageId;

  /**
   * Custom resize calculation for page items.
   * - Left handle: allows negative start times during drag (normalized on release).
   * - Right handle: no upper limit.
   */
  const handleCalculateResize = useCallback(
    (params: ResizeCalculationParams): ResizeCalculationResult => {
      const {originalStartTime, originalEndTime, deltaPixels, handle, pixelsPerSecond} = params;
      const deltaTime = deltaPixels / pixelsPerSecond;

      if (handle === 'left') {
        const newStart = Math.min(originalStartTime + deltaTime, originalEndTime - minPageDuration);
        return {startTime: newStart, endTime: originalEndTime};
      }

      const newEnd = Math.max(originalEndTime + deltaTime, originalStartTime + minPageDuration);
      return {startTime: originalStartTime, endTime: newEnd};
    },
    [minPageDuration]
  );

  return (
    <TimelineItem
      id={id}
      startTime={startTime}
      endTime={endTime}
      minDuration={minPageDuration}
      subRow={subRow}
      variant={TimelineItemType.PAGE}
      isSelected={isSelected}
      onClick={onClick}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onResizeEnd={onResizeEnd}
      onCalculateResize={handleCalculateResize}
    >
      <PageThumbnail pageId={id} isSelected={isSelected} />
      <div className={`${styles.content} ${isCurrentPage ? styles.currentPage : ''}`}>
        <div className={styles.pageInfo}>
          <Icon icon="icon-page" size={IconSize.SIZE_ICON_12} className={styles.icon} />
        </div>
      </div>
    </TimelineItem>
  );
}
